import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
} from "@mui/material";

import { Visibility, VisibilityOff, Email, Margin } from "@mui/icons-material";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { clearMessage, loginUser } from "../../redux/features/AuthSlice";
import { BeatLoader } from "react-spinners";
import TransitionsModal from "../../components/Modal";
import axiosInstance from "../../config/configAxios";

const LoginScreen = () => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [forgotModalOpen, setForgotModalOpen] = useState(false);
    const { error, success } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Email must be valid")
                .required("Email is required"),
            password: Yup.string()
                .required("Password is required")
                .min(6, "Minimum 6 characters"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await dispatch(loginUser(values));
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearMessage());
        }

        if (success) {
            toast.success(success);
            navigate("/");
            dispatch(clearMessage());
        }
    }, [error, success, dispatch, navigate]);

    const forgotFormik = useFormik({
        initialValues: {
            email: "",
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email("Email must be valid")
                .required("Email is required"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const response = await axiosInstance.post("/reset-password", values);
                toast.success(response.data.message);
                setForgotModalOpen(false);
                forgotFormik.resetForm();
            } catch (error) {
                toast.error(error.response?.data?.error || "Failed to send reset email");
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#f4f6f9",
            }}
        >
            <Box
                sx={{
                    width: 380,
                    p: 4,
                    background: "#fff",
                    borderRadius: "10px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    textAlign: "center",
                }}
            >
                {/* Logo */}
                <Box>
                    <img
                        src="https://images.vexels.com/media/users/3/142789/isolated/preview/2bfb04ad814c4995f0c537c68db5cd0b-multicolor-swirls-circle-logo.png"
                        alt="logo"
                        style={{ width: 100, margin: "0 auto", display: "block" }}
                    />
                </Box>

                {/* Title */}
                <Typography fontWeight="bold" fontSize={18}>
                    Sign in to start your session
                </Typography>

                <Typography fontSize={13} color="text.secondary" mb={2}>
                    Please enter your email and password to sign in
                </Typography>

                <form onSubmit={formik.handleSubmit}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        size="small"
                        name="email"
                        placeholder="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Email fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Password */}
                    <TextField
                        fullWidth
                        size="small"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password && Boolean(formik.errors.password)}
                        helperText={formik.touched.password && formik.errors.password}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Remember + Forgot */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                    >
                        <FormControlLabel
                            control={<Checkbox size="small" />}
                            label={<Typography fontSize={13}>Remember Me</Typography>}
                        />

                        <Typography
                            fontSize={13}
                            sx={{ cursor: "pointer", color: "#1976d2" }}
                            onClick={() => setForgotModalOpen(true)}
                        >
                            Forgot password?
                        </Typography>
                    </Box>

                    {/* Button */}
                    <Button
                        type="submit"
                        fullWidth
                        disabled={formik.isSubmitting}
                        sx={{
                            background: "#1976d2",
                            textTransform: "none",
                            py: 1,
                            fontWeight: "bold",
                            color: "#fff",
                            "&:hover": {
                                background: "#1565c0", // 🔥 hover color fix
                            },
                            "&.Mui-disabled": {
                                background: "#1976d2", // 🔥 disable e o same color
                                color: "#fff",
                                opacity: 0.7,
                            },
                        }}
                    >
                        {formik.isSubmitting ? (
                            <BeatLoader color="#fff" size={8} />
                        ) : (
                            "Sign In"
                        )}
                    </Button>
                </form>
            </Box>

            <TransitionsModal
                open={forgotModalOpen}
                 maxWidth="xs"
                 width=""
                setOpen={setForgotModalOpen}
                title="Forgot Password"
            >
                <Box sx={{ mb: 2, textAlign: "center" }}>
                    <img
                        src="https://images.vexels.com/media/users/3/142789/isolated/preview/2bfb04ad814c4995f0c537c68db5cd0b-multicolor-swirls-circle-logo.png"
                        alt="logo"
                        style={{ width: 80, margin: "0 auto", display: "block" }}
                    />
                </Box>
                <Typography variant="h6" component="h2" mb={2}>
                    Enter your email to reset password
                </Typography>
                <form onSubmit={forgotFormik.handleSubmit}>
                    <TextField
                        fullWidth
                        size="small"
                        name="email"
                        placeholder="Email"
                        value={forgotFormik.values.email}
                        onChange={forgotFormik.handleChange}
                        onBlur={forgotFormik.handleBlur}
                        error={forgotFormik.touched.email && Boolean(forgotFormik.errors.email)}
                        helperText={forgotFormik.touched.email && forgotFormik.errors.email}
                        FormHelperTextProps={{
                            sx: {
                                marginLeft: "0px",
                            },
                        }}
                        sx={{ mb: 2 }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Email fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        disabled={forgotFormik.isSubmitting}
                        sx={{
                            background: "#1976d2",
                            textTransform: "none",
                            py: 1,
                            fontWeight: "bold",
                            color: "#fff",
                            "&:hover": {
                                background: "#1565c0",
                            },
                            "&.Mui-disabled": {
                                background: "#1976d2",
                                color: "#fff",
                                opacity: 0.7,
                            },
                        }}
                    >
                        {forgotFormik.isSubmitting ? (
                            <BeatLoader color="#fff" size={8} />
                        ) : (
                            "Send Reset Link"
                        )}
                    </Button>
                </form>
            </TransitionsModal>
        </Box>
    );
};

export default LoginScreen;
